import React, { useEffect, useRef, useMemo, useState } from 'react';
import { Bot, Eraser, Play, Loader2, Check, X, AlertTriangle } from 'lucide-react';
import { ChatMessage } from './ChatMessage';
import { ChatInput } from './ChatInput';
import { PlanDesignControls } from './PlanDesignControls';
import { PhaseControls } from './PhaseControls';
import { ChatShortcuts } from './ChatShortcuts';
import { useAgentLoop } from './useAgentLoop';
import TokenWarningModal from '../common/TokenWarningModal';
import { ReasoningMarquee } from '../common/ReasoningMarquee';

type ChatInputRef = { focus: () => void; };
type Message = { id?: number; role: 'user' | 'assistant'; content: string; input_tokens?: number; output_tokens?: number; input_money?: number; output_money?: number; total_money?: number; reasoningText?: string; isThinking?: boolean; partial?: boolean; partialReason?: string; };
type SysPrompt = { id: number; name: string; category?: string; };

type Props = {
  messages: Message[]; 
  prompts: SysPrompt[]; 
  selectedPromptId: number | null;
  onSelectPrompt: (id: number) => void; 
  onStart: () => void; 
  onClearHistory: () => void;
  isGenerating: boolean; 
  input: string; 
  setInput: (val: string) => void; 
  onSend: () => void;
  onDirectSend: (text: string) => void; 
  onAddToDraft: (text: string) => void;
  onDeletePair: (idx: number, id?: number, skipConfirm?: boolean) => Promise<boolean>;
  onDeleteSingle: (idx: number, id?: number, skipConfirm?: boolean) => Promise<boolean>;
  isConnected: boolean; 
  hasLessonInfo: boolean; 
  isCurrentUserOwner?: boolean;
  isRightPanelCollapsed?: boolean;
  onAppendContent?: (text: string) => void;
  onSaveContent?: () => void;
  documentContent?: string;
  isCollapsed?: boolean;
  onExport?: (content: string) => void;
  courseId?: number | null;
  phaseCount?: number;
  currentReasoning?: string;
  onStopGeneration: () => void;
};

export const ChatArea: React.FC<Props> = ({
  messages,
  prompts,
  selectedPromptId,
  onSelectPrompt,
  onStart,
  onClearHistory,
  isGenerating,
  input,
  setInput,
  onSend,
  onDirectSend,
  onAddToDraft,
  onDeletePair,
  onDeleteSingle,
  isConnected,
  hasLessonInfo,
  isCurrentUserOwner = false,
  isRightPanelCollapsed = false,
  onAppendContent,
  onSaveContent,
  documentContent = '',
  isCollapsed = false,
  onExport,
  courseId = null,
  phaseCount = 7,
  currentReasoning = '',
  onStopGeneration,
}) => {
  // 接入升级后的外脑 Hook
  const { 
    autoLoopState, 
    setAutoLoopState, 
    currentLoopPhase, 
    setCurrentLoopPhase, 
    loopCount, 
    setLoopCount, 
    MAX_LOOPS, 
    totalParts,
    getStatusText,
    toastMessage,
    setToastMessage
  } = useAgentLoop(isGenerating, messages, onDirectSend, onAppendContent, onSaveContent, phaseCount);

  const displayMaxLoops = autoLoopState.startsWith('LESSON_PLAN_P') ? totalParts : MAX_LOOPS;

  const scrollRef = useRef<HTMLDivElement>(null);
  const chatInputRef = useRef<ChatInputRef>(null);
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [showTokenWarning, setShowTokenWarning] = useState(false);
  const [warningTokenCount, setWarningTokenCount] = useState(0);
  
  const currentPrompt = useMemo(() => prompts.find(p => p.id === selectedPromptId), [prompts, selectedPromptId]);
  const isAuditMode = useMemo(() => !!currentPrompt?.category?.toLowerCase().startsWith('audit') || !!currentPrompt?.name.toLowerCase().startsWith('audit'), [currentPrompt]);
  
  const handleSendMessage = () => {
    setAutoLoopState('IDLE'); 
    setLoopCount(0);
    // 如果需要强制带特定字眼的验证可以在这里恢复判断逻辑
    onSend(); 
    setShowAlert(false);
  };

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  useEffect(() => {
    if (messages.length > 0 && !isGenerating) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.role === 'assistant' && lastMessage.input_tokens && lastMessage.input_tokens >= 190000) {
        setWarningTokenCount(lastMessage.input_tokens);
        setShowTokenWarning(true);
      }
    }
  }, [messages, isGenerating]);

  return (
    <div className={`flex flex-col bg-white relative transition-all duration-300 overflow-hidden ${
      // 💎 核心布局动态计算：配合右侧区域状态，实现丝滑折叠与满屏展开
      isCollapsed 
        ? 'w-0 flex-none opacity-0 pointer-events-none border-none' 
        : `border-r border-gray-200 ${isRightPanelCollapsed ? 'flex-1 max-w-none min-w-0' : 'flex-1 max-w-[50%] min-w-[400px]'}`
    }`}>
      
      {/* Toast 提示：循环迭代结束或需要人工介入 */}
      {toastMessage && (
        <div className={`absolute top-4 left-4 z-30 flex items-center gap-3 px-4 py-3 rounded-lg shadow-xl text-white transform transition-all animate-in slide-in-from-left-10 duration-300 ${
          toastMessage.type === 'success' ? 'bg-green-600' : 'bg-amber-500'
        }`}>
          {toastMessage.type === 'success' ? <Check size={18} /> : <AlertTriangle size={18} />}
          <span className="font-medium text-sm">{toastMessage.text}</span>
          <button 
            onClick={() => setToastMessage(null)} 
            className="ml-2 hover:bg-white/20 p-1 rounded"
          >
            <X size={14}/>
          </button>
        </div>
      )}
      
      <div className="flex-1 overflow-y-auto p-4 space-y-6 pb-20" ref={scrollRef}>
        {messages.length === 0 && (
          <div className="h-full flex flex-col items-center justify-center text-gray-400 opacity-50">
            <Bot size={48} className="mb-4"/>
            <p>Select a prompt and click Start.</p>
          </div>
        )}
        
        {messages.map((msg, idx) => (
          <ChatMessage 
            key={idx} 
            msg={msg} 
            index={idx} 
            onAddToDraft={onAddToDraft}
            onDeletePair={onDeletePair}
            onDeleteSingle={onDeleteSingle}
            isCurrentUserOwner={isCurrentUserOwner}
            onExport={onExport}
          />
        ))}
        

      </div>

      <div className="bg-gray-50 border-t p-4 space-y-3 relative">
        {/* 走马灯字幕：reasoning/progress 在底部输入区上方显示 */}
        {currentReasoning && (
          <ReasoningMarquee text={currentReasoning} variant="reasoning" />
        )}

        {/* 动态横幅：Agent运行状态提示 */}
        {autoLoopState !== 'IDLE' && (
           <div className="absolute -top-12 left-0 bg-blue-50 border border-blue-200 text-blue-800 px-5 py-2.5 rounded-full text-xs font-semibold shadow-lg flex items-center gap-3 animate-bounce z-20 whitespace-nowrap">
             <Loader2 size={16} className="animate-spin text-blue-600" />
             <span>Agent 引擎运行中 [第 <strong className="text-blue-600">{loopCount + 1}/{displayMaxLoops}</strong> 轮] : <span className="text-indigo-600">{getStatusText()}</span></span>
             <div className="h-4 w-px bg-blue-300 mx-1"></div>
             <button 
               onClick={() => { setAutoLoopState('IDLE'); setLoopCount(0); }} 
               className="text-red-500 hover:text-red-700 hover:bg-red-50 px-2 py-0.5 rounded transition-colors"
             >
               强制阻断
             </button>
           </div>
        )}

        {/* 顶部工具栏 */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            {messages.length === 0 && (
              <button 
                onClick={onStart} 
                disabled={!isConnected || isGenerating || !selectedPromptId || !hasLessonInfo} 
                className={`px-5 py-2 rounded-lg text-sm font-medium flex items-center gap-2 text-white transition-colors shadow-sm active:scale-95 disabled:opacity-50 ${isAuditMode ? 'bg-purple-600 hover:bg-purple-700' : 'bg-brand-600 hover:bg-brand-700'}`}
              >
                <Play size={16} fill="currentColor"/> {isAuditMode ? 'Start Audit' : 'Start'}
              </button>
            )}
            
            {messages.length > 0 && (
              <button 
                onClick={onClearHistory} 
                className="p-2 bg-white text-gray-400 hover:text-red-600 border border-gray-200 rounded-full shadow-md"
              >
                <Eraser size={18} />
              </button>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            {messages.length > 0 && (
              <>
                <button
                  onClick={() => {
                    setAutoLoopState('LESSON_PLAN_P1');
                    setLoopCount(0);
                    const totalParts = phaseCount === 10 ? 6 : 5;
                    onDirectSend(`根据最完整详细版本的教案模板中对于教案目录结构、标题名称、篇幅要求，总结以上所有${phaseCount}个环节设计的最终定稿版详细方案设计，生成本课最终完整版详细教案。然后分${totalParts === 6 ? '六' : '五'}部分返回完整详细教案。首先仅返回完整详细教案的第一部分。`);
                  }}
                  disabled={!isConnected || isGenerating}
                  className="px-3 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-bold rounded-lg shadow-sm hover:shadow-md transition-all disabled:opacity-50 whitespace-nowrap"
                >
                  🏆 一键生成完整教案
                </button>
                <button
                  onClick={() => onDirectSend('审核完整教案')}
                  disabled={!documentContent?.trim() || !isConnected || isGenerating}
                  className={`px-3 py-2 text-xs font-bold rounded-lg shadow-sm transition-all whitespace-nowrap ${
                    documentContent?.trim() && isConnected && !isGenerating
                      ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white hover:shadow-md cursor-pointer'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  📋 审核完整教案
                </button>
              </>
            )}
          </div>
        </div>

        {/* 快捷输入 - 隐藏调用 */}
        <div className="hidden">
          <ChatShortcuts 
            input={input} 
            setInput={setInput} 
            chatInputRef={chatInputRef} 
            isConnected={isConnected} 
            isGenerating={isGenerating} 
            isAuditMode={isAuditMode}
            phaseCount={phaseCount}
          />
        </div>
        
        {/* 霸气的控制组件群 */}
        <div className="flex flex-col gap-1.5 mt-2">
          {/* 整体方案设计 */}
          <PlanDesignControls 
            isConnected={isConnected} 
            isGenerating={isGenerating} 
            isAuditMode={isAuditMode} 
            setAutoLoopState={setAutoLoopState} 
            setLoopCount={setLoopCount} 
            onDirectSend={onDirectSend} 
          />
          
          {/* 环节一 到 环节N 的通用控制台 及 终极生成按钮 */}
          <PhaseControls 
            isConnected={isConnected} 
            isGenerating={isGenerating} 
            setAutoLoopState={setAutoLoopState} 
            setCurrentLoopPhase={setCurrentLoopPhase} 
            setLoopCount={setLoopCount} 
            onDirectSend={onDirectSend}
            phaseCount={phaseCount}
          />
        </div>
        
        {/* 输入框 */}
        <div className="relative mt-2">
          <ChatInput
            ref={chatInputRef}
            input={input}
            setInput={setInput}
            onSend={handleSendMessage}
            isGenerating={isGenerating}
            disabled={!isConnected}
            onStopGeneration={onStopGeneration}
          />
          {showAlert && (
            <div className="absolute top-0 left-0 -translate-y-full mb-2 px-4 py-2 bg-red-50 text-red-600 rounded-lg text-sm shadow-md border border-red-100 z-10">
              必须指定'但仅生成'什么具体内容
            </div>
          )}
        </div>
      </div>

      {/* Token 超限警告模态框 */}
      <TokenWarningModal
        isOpen={showTokenWarning}
        onClose={() => setShowTokenWarning(false)}
        currentTokens={warningTokenCount}
      />
    </div>
  );
};